import { EnhancedDataTable } from "adizen-tanstack-table";
import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  activateCourse,
  deactivateCourse,
  deleteCourse,
  getCourses,
} from "../../../api/courseApi";
import {
  BookOpen,
  Eye,
  MoreHorizontal,
  AlertTriangle,
  Play,
  Clock,
  BookMarked,
  Plus,
  Pause,
} from "lucide-react";
import { authStore } from "@/stores/authStore";
import { permissionStore, PermissionAction, ResourceType } from "@/stores/permissionStore";
import { useSnapshot } from "valtio";
import { getApiErrorMessage } from "@/utils/apiErrorMessage";

export default function ManageCourses() {
  const snap = useSnapshot(authStore);
  const permSnap = useSnapshot(permissionStore);
  const navigate = useNavigate();
  const isAdmin =
    snap?.user?.role === "ADMIN" || snap?.user?.role === "SUPER_ADMIN";
  
  // Permission checks
  const canCreateCourse = permSnap.can(PermissionAction.CREATE, ResourceType.COURSE);
  const canUpdateCourse = permSnap.can(PermissionAction.UPDATE, ResourceType.COURSE);
  const canReadCourse = permSnap.can(PermissionAction.READ, ResourceType.COURSE);
  const canDeleteCourse = permSnap.can(PermissionAction.DELETE, ResourceType.COURSE);

  // Delete dialog state
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [courseDeactivate, setCourseDeactivate] = useState(null);

  // Rows from the last successful fetch, kept in a ref so lookups don't cost a
  // render. The table owns the authoritative copy; this is just for row-level
  // actions that need the course name.
  const rowsRef = useRef([]);
  // Bumped to force the table to remount and refetch after a mutation.
  const [tableRefreshKey, setTableRefreshKey] = useState(0);
  const refreshTable = useCallback(() => setTableRefreshKey((k) => k + 1), []);

  const handleCourseAction = async (action, courseId) => {
    if (action === "VIEW") {
      sessionStorage.setItem("courseId", courseId);
      // Find course name from the data
      const course = rowsRef.current.find((c) => c.id === courseId);
      navigate(`/admin/course-builder/${courseId}`, {
        state: { courseId, action, courseName: course?.name },
      });
    } else if (action === "DEACTIVATE") {
      await deactivateCourse(courseId);
      refreshTable();
    } else if (action === "ACTIVATE") {
      await activateCourse(courseId);
      refreshTable();
    }
  };

  // Must be referentially stable: the table's internal initial-fetch effect
  // depends on the identity of this callback, so a new function per render
  // re-triggers it and fires duplicate requests.
  const fetchCourses = useCallback(async (params) => {
    try {
      const {
        page = 1,
        limit = 10,
        search,
        sortBy,
        sortOrder,
        isActive,
        tags,
        createdAfter,
        createdBefore,
        ...otherParams
      } = params || {};

      // Build filters object for API
      const filters = {
        page,
        limit,
        sortBy: sortBy || "createdAt",
        sortOrder: sortOrder || "asc",
      };

      // Add search if provided
      if (search) {
        filters.search = search;
      }

      // Handle isActive filter
      if (isActive && isActive !== "all") {
        filters.isActive = isActive === "active";
      }

      // Handle tags filter
      if (tags && Array.isArray(tags) && tags.length > 0) {
        filters.tags = tags;
      } else if (tags && typeof tags === "string" && tags !== "all") {
        filters.tags = [tags];
      }

      // Handle date filters
      if (createdAfter) {
        filters.createdAfter = new Date(createdAfter);
      }
      if (createdBefore) {
        filters.createdBefore = new Date(createdBefore);
      }

      // Add any other parameters that weren't explicitly handled
      Object.keys(otherParams).forEach((key) => {
        if (otherParams[key] && otherParams[key] !== "all") {
          filters[key] = otherParams[key];
        }
      });

      const response = await getCourses(filters);

      if (response.success && Array.isArray(response.data)) {
        const mappedCourses = response.data.map((course) => ({
          id: course.id,
          name: course.name,
          description: course.description,
          tags: course.tags || [],
          isActive: course.isActive,
          status: course.isActive ? "active" : "inactive",
          coverImage: course.coverImage
            ? import.meta.env.VITE_CDN_LINK + course.coverImage
            : null,
          createdAt: course.createdAt,
          updatedAt: course.updatedAt,
          topicsCount: course.topics?.length || 0,
          materialsCount:
            course.topics?.reduce(
              (acc, topic) => acc + (topic.learningMaterials?.length || 0),
              0
            ) || 0,
          courseDuration: course.courseDuration || 0,
          passingScore: course.passingScore || "0",
          hasAssessment: !!course.courseAssessment,
          // Calculated fields
          estimatedDuration:
            course.topics?.reduce(
              (acc, topic) => acc + (topic.estimatedDuration || 0),
              0
            ) || 0,
          // Raw course data for actions
          _raw: course,
        }));

        rowsRef.current = mappedCourses;

        return {
          success: true,
          message: response.message,
          data: mappedCourses,
          meta: {
            total: response.pagination?.total || mappedCourses.length,
            page: response.pagination?.page || page,
            limit: response.pagination?.limit || limit,
            totalPages:
              response.pagination?.totalPages ||
              Math.ceil(mappedCourses.length / limit),
            hasNext: response.pagination?.hasNext || false,
            hasPrev: response.pagination?.hasPrev || false,
          },
        };
      } else {
        throw new Error(response.message || "Failed to fetch courses");
      }
    } catch (error) {
      console.error("Error fetching courses:", error);
      toast.error(getApiErrorMessage(error, "Failed to fetch courses"));
      return {
        success: false,
        message: error.message,
        data: [],
        meta: {
          total: 0,
          page: 1,
          limit: 10,
          totalPages: 0,
          hasNext: false,
          hasPrev: false,
        },
      };
    }
  }, []);

  // Built here rather than via useServerSideTableData: that hook returns a new
  // config object (and a new onDataRequest closure) on every render, which
  // re-triggers the table's internal fetch effect and produces duplicate
  // requests. A memoised config keeps the identity stable.
  const tableServerSide = useMemo(
    () => ({ enabled: true, onDataRequest: fetchCourses, debounceMs: 500 }),
    [fetchCourses]
  );

  const handleDeactivateCourse = async () => {
    if (!courseDeactivate) return;
    await handleCourseAction(
      courseDeactivate.isActive ? "DEACTIVATE" : "ACTIVATE",
      courseDeactivate.id
    );
    setDeactivateDialogOpen(false);
  };

  const handleDialogClose = () => {
    setDeactivateDialogOpen(false);
    setCourseDeactivate(null);
  };

  // Format duration in minutes to readable format
  const formatDuration = (minutes) => {
    if (!minutes || minutes === 0) return "-";
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}m`
      : `${hours}h`;
  };

  const columns = useMemo(() => {
    return [
      {
        id: "serialNumber",
        header: "S.No",
        accessorFn: (_, index) => index + 1,
        enableGlobalFilter: false,
        enableColumnFilter: false,
        enableSorting: false,
        size: 60,
      },
      {
        id: "name",
        header: "Course Name",
        accessorKey: "name",
        accessorFn: (course) => course.name,
        enableGlobalFilter: true,
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "text",
        serverFilterKey: "search",
        serverSortKey: "name",
        size: 300,
        cell: ({ row }) => {
          const course = row.original;
          return (
            <div className="flex items-start gap-3">
              {course.coverImage ? (
                <img
                  src={course.coverImage}
                  alt={course.name}
                  className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <BookOpen className="h-6 w-6 text-gray-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{course.name}</p>
                <p className="text-xs text-gray-500 line-clamp-2 mt-1">
                  {course.description}
                </p>
              </div>
            </div>
          );
        },
        exportConfig: {
          exportHeader: "Course Name",
          exportValueFn: (course) => course.name,
        },
      },
      {
        id: "tags",
        header: "Tags",
        accessorKey: "tags",
        accessorFn: (course) => course.tags.join(", "),
        enableGlobalFilter: true,
        enableColumnFilter: true,
        filterType: "text",
        serverFilterKey: "tags",
        serverSortKey: "tags",
        size: 200,
        cell: ({ row }) => {
          const tags = row.original.tags;
          if (!tags || tags.length === 0) return "-";

          const firstLine = tags.slice(0, 2);
          const remaining = tags.slice(2);

          return (
            <div className="flex flex-col gap-1">
              <div className="flex flex-wrap gap-1">
                {firstLine.map((tag, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="text-xs border bg-[#D9ECF8] text-[#2B6A7F]"
                  >
                    {tag}
                  </Badge>
                ))}
                {remaining.length > 0 && (
                  <Badge variant="outline" className="text-xs">
                    +{remaining.length}
                  </Badge>
                )}
              </div>
            </div>
          );
        },
        exportConfig: {
          exportHeader: "Tags",
          exportValueFn: (course) => course.tags.join(", "),
        },
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "status",
        accessorFn: (course) => course.status,
        enableGlobalFilter: false,
        enableColumnFilter: true,
        filterType: "select",
        filterOptions: [
          { label: "Active", value: "active" },
          { label: "Inactive", value: "inactive" },
        ],
        serverFilterKey: "isActive",
        serverSortKey: "isActive",
        size: 100,
        cell: ({ getValue }) => {
          const status = getValue();
          return (
            <Badge
              className={
                status === "active"
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-800"
              }
            >
              {status.toUpperCase()}
            </Badge>
          );
        },
        exportConfig: {
          exportHeader: "Status",
          exportValueFn: (course) => course.status,
        },
      },
      {
        id: "topicsCount",
        header: "Topics",
        accessorKey: "topicsCount",
        accessorFn: (course) => course.topicsCount,
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        serverFilterKey: "topicsCount",
        serverSortKey: "topicsCount",
        size: 80,
        cell: ({ getValue }) => {
          const count = getValue();
          return (
            <div className="flex items-center gap-1">
              <BookMarked className="h-4 w-4 text-gray-400" />
              <span>{count}</span>
            </div>
          );
        },
        exportConfig: {
          exportHeader: "Topics Count",
          exportValueFn: (course) => course.topicsCount,
        },
      },
      {
        id: "materialsCount",
        header: "Materials",
        accessorKey: "materialsCount",
        accessorFn: (course) => course.materialsCount,
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        serverFilterKey: "materialsCount",
        serverSortKey: "materialsCount",
        size: 100,
        cell: ({ getValue }) => {
          const count = getValue();
          return (
            <div className="flex items-center gap-1">
              <Play className="h-4 w-4 text-gray-400" />
              <span>{count}</span>
            </div>
          );
        },
        exportConfig: {
          exportHeader: "Materials Count",
          exportValueFn: (course) => course.materialsCount,
        },
      },
      {
        id: "courseDuration",
        header: "Duration",
        accessorKey: "courseDuration",
        accessorFn: (course) => course.courseDuration,
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        serverFilterKey: "courseDuration",
        serverSortKey: "courseDuration",
        size: 100,
        cell: ({ getValue }) => {
          const duration = getValue();
          return (
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4 text-gray-400" />
              <span>{formatDuration(duration)}</span>
            </div>
          );
        },
        exportConfig: {
          exportHeader: "Duration",
          exportValueFn: (course) => formatDuration(course.courseDuration),
        },
      },
      {
        id: "passingScore",
        header: "Passing Score",
        accessorKey: "passingScore",
        accessorFn: (course) => course.passingScore,
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "number",
        serverFilterKey: "passingScore",
        serverSortKey: "passingScore",
        size: 120,
        cell: ({ getValue }) => {
          const score = getValue();
          return <Badge variant="outline">{score}%</Badge>;
        },
        exportConfig: {
          exportHeader: "Passing Score",
          exportValueFn: (course) => course.passingScore,
        },
      },
      {
        id: "hasAssessment",
        header: "Assessment",
        accessorKey: "hasAssessment",
        accessorFn: (course) => course.hasAssessment,
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "select",
        filterOptions: [
          { label: "Has Assessment", value: "true" },
          { label: "No Assessment", value: "false" },
        ],
        serverFilterKey: "hasAssessment",
        serverSortKey: "hasAssessment",
        size: 120,
        cell: ({ getValue }) => {
          const hasAssessment = getValue();
          return (
            <Badge
              className={
                hasAssessment
                  ? "bg-[#D9ECF8] text-[#2B6A7F]"
                  : "bg-gray-100 text-gray-800"
              }
            >
              {hasAssessment ? "Yes" : "No"}
            </Badge>
          );
        },
        exportConfig: {
          exportHeader: "Has Assessment",
          exportValueFn: (course) => (course.hasAssessment ? "Yes" : "No"),
        },
      },
      {
        id: "createdAt",
        header: "Created Date",
        accessorKey: "createdAt",
        accessorFn: (course) => course.createdAt,
        enableGlobalFilter: false,
        enableSorting: true,
        enableColumnFilter: false,
        filterType: "date",
        serverFilterKey: "createdAfter",
        serverSortKey: "createdAt",
        size: 150,
        cell: ({ getValue }) => {
          return new Date(getValue()).toLocaleDateString();
        },
        exportConfig: {
          exportHeader: "Created Date",
          exportValueFn: (course) =>
            new Date(course.createdAt).toLocaleDateString(),
        },
      },
      {
        id: "updatedAt",
        header: "Last Updated",
        accessorKey: "updatedAt",
        accessorFn: (course) => course.updatedAt,
        enableSorting: true,
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "date",
        serverFilterKey: "updatedAfter",
        serverSortKey: "updatedAt",
        size: 150,
        cell: ({ getValue }) => {
          return new Date(getValue()).toLocaleDateString();
        },
        exportConfig: {
          exportHeader: "Last Updated",
          exportValueFn: (course) =>
            new Date(course.updatedAt).toLocaleDateString(),
        },
      },
      {
        id: "actions",
        header: "Actions",
        accessorKey: "actions",
        enableGlobalFilter: false,
        enableColumnFilter: false,
        enableSorting: false,
        size: 100,
        cell: ({ row }) => {
          const course = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">Open menu</span>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {canReadCourse && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.preventDefault();
                      handleCourseAction("VIEW", course.id);
                    }}
                    className="cursor-pointer"
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    <span>View Details</span>
                  </DropdownMenuItem>
                )}

                {canUpdateCourse && (
                  <>
                    {canReadCourse && <DropdownMenuSeparator />}
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        setCourseDeactivate(course);
                        setDeactivateDialogOpen(true);
                      }}
                    >
                      {course.isActive ? (
                        <div className="flex items-center gap-2 text-red-600">
                          <Pause className="h-4 w-4" />
                          Deactivate
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-[#2B6A7F]">
                          <Play className="h-4 w-4" />
                          Activate
                        </div>
                      )}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
        exportConfig: {
          excludeFromExport: true,
        },
      },
    ];
  }, [canCreateCourse, canUpdateCourse, canReadCourse, canDeleteCourse]);

  // If user doesn't have READ permission, show message
  if (!canReadCourse) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to view courses.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        <div className="overflow-hidden font-sans">
          <div className="w-full flex flex-col gap-4 ">
            <section className="w-full flex flex-col border bg-white p-4 rounded-xl shadow-xl gap-4">
              <div className="flex justify-between items-center">
                <h3 className="uppercase text-lg text-neutral-800 font-bold">
                  Course Directory
                </h3>
                {canCreateCourse && (
                  <Button
                    onClick={() => {
                      localStorage.removeItem("courseData");
                      localStorage.removeItem("topics");
                      localStorage.removeItem("assessment");
                      localStorage.removeItem("courseBuilderState");
                      navigate("/admin/course-builder");
                    }}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Create Course
                  </Button>
                )}
              </div>
              <div>
                <EnhancedDataTable
                  key={`courses-table-${tableRefreshKey}`}
                  data={[]}
                  columns={columns}
                  emptyMessage="No courses found"
                  enableDateRangeFilter={true}
                  dateFilterField="createdAt"
                  enableGlobalSearch={true}
                  globalSearchPlaceholder="Search courses..."
                  enableColumnVisibility={true}
                  enablePagination={true}
                  initialPageSize={10}
                  pageSizeOptions={[5, 10, 20, 50, 100]}
                  enableCSVExport={true}
                  enableCustomViews={true}
                  viewStorageKey={"courses-management-view"}
                  onViewChange={(view) => {
                    console.log("Courses view changed:", view);
                  }}
                  serverSide={tableServerSide}
                  theme="light"
                />
              </div>
            </section>
          </div>
        </div>
      </div>
      {/* Delete Confirmation Dialog */}
      <Dialog open={deactivateDialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <div className="flex items-center space-x-2">
              <div className="rounded-full bg-red-100 p-2">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <DialogTitle>
                {courseDeactivate?.isActive ? "Deactivate" : "Activate"} Course
              </DialogTitle>
            </div>
            <DialogDescription className="pt-2">
              Are you sure you want to{" "}
              {courseDeactivate?.isActive ? "deactivate" : "activate"} course "
              {courseDeactivate?.name}"? This action cannot be undone and will
              also delete all associated topics and materials.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-end">
            <Button variant="outline" onClick={handleDialogClose}>
              Cancel
            </Button>
            <Button
              className={
                courseDeactivate?.isActive
                  ? "bg-red-500 text-white"
                  : "bg-[#2B6A7F] text-white"
              }
              onClick={handleDeactivateCourse}
            >
              {courseDeactivate?.isActive ? "Deactivate" : "Activate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
